import React, { useState, useEffect } from 'react'
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { SelectValidator, TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import { InputAdornment } from '@mui/material';
import { getLocalNumberLength, getPartner } from '../../../../../lib/access.es6';
import { RELATIONSHIP_POOL } from "../../../../../containers/NewProductList/PoductConstants";
import _ from "lodash";
import { getPartnerGuid } from "../../../../../lib/access.es6";

const ClaimLoggingDetails = (props) => {
  const classes = useStyle();

  const [name, setName] = useState(props.claimState.claimant_full_name || "");
  const [phoneNumber, setPhoneNumber] = useState(props.claimState.claimant_phone_number || "");
  const [idNumber, setIdNumber] = useState(props.claimState.claimant_id_number || "");
  const [relationship, setRelationship] = useState(props.claimState.claimant_relationship_to_patient || null);
  const [member, setMember] = useState("");
  const [email, setEmail] = useState(props.claimState.claimant_email || '');
  const [paymentOption, setPaymentOption] = useState(props.claimState.claimant_payment_option || '');
  const [bankName, setBankName] = useState(props.claimState.claimant_bank_name || '');
  const [accountNumber, setAccountNumber] = useState(props.claimState.claimant_bank_account_number || '');
  const [mpesaNumber, setMpesaNumber] = useState(props.claimState.claimant_mpesa_phone_number || '');
  const [claimant_bank_account_holder, setClaimantBankAccountHolder] = useState(props.claimState.claimant_bank_account_holder || '');
  const [claimant_bank_branch_code, setClaimantBankBranchCode] = useState(props.claimState.claimant_bank_branch_code || '');
  const [claimantMatchesDeceasedError, setClaimantMatchesDeceasedError] = useState('');
  const [claimantRelationshipMismatchError, setClaimantRelationshipMismatchError] = useState('');

  const partnerGuid = getPartnerGuid();
  const OldMutualKenyaPaymentOptions = ["Bank", "Mpesa"];
  const OldMutualFLPPaymentOptions = ["Bank"];

  // A HospiCash claim has no deceased, so all "deceased" concepts below only
  // apply to an OMM funeral (death) claim.
  const isHospiCashClaim = props.hasHospiCashIllnessBenefit || props.hasHospiCashAccidentBenefit;
  const isOmmFuneralClaim = partnerGuid === 'old_mutual_malawi' && !isHospiCashClaim;

  const members = props.eventQuote.policy_schedule_entries
    ? props.eventQuote.policy_schedule_entries
    : [];

  const deceasedDependant = (isOmmFuneralClaim && props.claimInitiationPayload.member)
    ? props.claimInitiationPayload.member.dependant
    : null;

  // Same source as deceasedDependant (SelectMember.js sets claimInitiationPayload.member
  // identically for both claim types), but available for HospiCash too - a claimant filing
  // for themselves is the normal case there (unlike funeral, where it's impossible), so this
  // is used to require "Self" rather than to block the match outright.
  const patientDependant = props.claimInitiationPayload.member
    ? props.claimInitiationPayload.member.dependant
    : null;

  const claimantDropdownMembers = (isOmmFuneralClaim && deceasedDependant)
    ? members.filter((entry) => entry.dependant.guid !== deceasedDependant.guid)
    : members;

  // "Self" isn't in the shared RELATIONSHIP_POOL (used by many other partners' unrelated
  // dependant/beneficiary forms, where it wouldn't make sense) - but the "Select from policy
  // member" auto-fill below sets exactly this value when the member is the Principal, for both
  // claim types. Added locally here only, so the auto-filled value has a matching option to
  // display instead of rendering blank, without adding "Self" anywhere else in the app.
  const relationshipOptions = [
    ['Self', 'Self'],
    ...Object.entries(RELATIONSHIP_POOL),
  ].sort(([, vA], [, vB]) => vA.localeCompare(vB));

  const normalizePhone = (phone) => {
    if (!phone) return '';
    return ('' + phone).replace(/\D/g, '').slice(-9);
  };

  const personMatches = (person, currentName, currentIdNumber, currentPhone) => {
    if (!person) return false;
    const pName = ((person.first_name || '') + ' ' + (person.last_name || '')).trim().toLowerCase();
    const clName = (currentName || '').trim().toLowerCase();
    if (clName && pName && clName === pName) return true;
    const pId = (person.national_id || '').trim().toLowerCase();
    const clId = (currentIdNumber || '').trim().toLowerCase();
    if (clId && pId && clId === pId) return true;
    const pPhone = normalizePhone(person.msisdn);
    const clPhone = normalizePhone(currentPhone);
    if (clPhone && pPhone && clPhone === pPhone) return true;
    return false;
  };

  const checkClaimantMatchesDeceased = (currentName, currentIdNumber, currentPhone) =>
    personMatches(deceasedDependant, currentName, currentIdNumber, currentPhone);

  const runDeceasedMatchCheck = (currentName, currentIdNumber, currentPhone) => {
    if (!isOmmFuneralClaim) return;
    const hasMatch = checkClaimantMatchesDeceased(currentName, currentIdNumber, currentPhone);
    setClaimantMatchesDeceasedError(hasMatch
      ? 'The claimant details match the deceased. The deceased and the claimant cannot be the same person.'
      : '');
    props.setAdditionalState('claimant_is_deceased', hasMatch);
  };

  // HospiCash-only: unlike funeral, the claimant CAN legitimately be the patient (filing for
  // themselves) - so this doesn't block a match, it requires consistency between whether the
  // claimant's details match the patient and whether "Self" is the selected relationship.
  const runClaimantRelationshipCheck = (currentName, currentIdNumber, currentPhone, currentRelationship) => {
    if (!isHospiCashClaim || !patientDependant) return;
    const matchesPatient = personMatches(patientDependant, currentName, currentIdNumber, currentPhone);
    let error = '';
    if (matchesPatient && currentRelationship && currentRelationship !== 'Self') {
      error = "The claimant's details match the patient's, so relationship to patient must be \"Self\".";
    } else if (!matchesPatient && currentRelationship === 'Self') {
      error = 'Relationship to patient is set to "Self" but the claimant\'s details do not match the patient\'s.';
    }
    setClaimantRelationshipMismatchError(error);
    props.setAdditionalState('claimant_relationship_mismatch', !!error);
  };

  useEffect(() => {
    ValidatorForm.addValidationRule("isValidPhoneNumber", (value) => {
      if (!value) {
        return false;
      }
      return value.length === getLocalNumberLength();
    });

    return function cleanup() {
      ValidatorForm.removeValidationRule("isValidPhoneNumber");
    };
  }, []);

  useEffect(() => {
    if (isOmmFuneralClaim) {
      runDeceasedMatchCheck(
        props.claimState.claimant_full_name || '',
        props.claimState.claimant_id_number || '',
        props.claimState.claimant_phone_number || ''
      );
    }
    if (isHospiCashClaim) {
      runClaimantRelationshipCheck(
        props.claimState.claimant_full_name || '',
        props.claimState.claimant_id_number || '',
        props.claimState.claimant_phone_number || '',
        props.claimState.claimant_relationship_to_patient || ''
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const internationalCallingCode = getPartner().country
    .international_calling_code;

  // Partner dialing code digits (e.g. "265" for Malawi). international_calling_code
  // carries the "00" IDD prefix (e.g. "00265"); strip it to the bare country code
  // so autopopulated msisdns like "+265..." reduce to the 9-digit local number.
  let dialingCode = (internationalCallingCode || '').toString().replace(/[^0-9]/g, '');
  if (dialingCode.indexOf('00') === 0) {
    dialingCode = dialingCode.slice(2);
  }

  // If the number already carries a country/dialing code, keep it; otherwise
  // prepend the partner (Malawi) dialing code. Returns a "00<code><local>" msisdn.
  const toFullMsisdn = (raw) => {
    if (!raw) return '';
    let digits = raw.toString().replace(/[^0-9]/g, '');
    if (digits.indexOf('00') === 0) return digits; // already 00<code>...
    if (dialingCode && digits.indexOf(dialingCode) === 0) return '00' + digits;
    if (digits.indexOf('0') === 0) digits = digits.slice(1); // strip trunk 0
    return dialingCode ? '00' + dialingCode + digits : digits;
  };

  // Local portion of a number for display (strips a leading 00<code> or <code>).
  const toLocalMsisdn = (raw) => {
    if (!raw) return '';
    let digits = raw.toString().replace(/[^0-9]/g, '');
    if (dialingCode && digits.indexOf('00' + dialingCode) === 0) {
      return digits.slice(2 + dialingCode.length);
    }
    if (dialingCode && digits.indexOf(dialingCode) === 0) {
      return digits.slice(dialingCode.length);
    }
    if (digits.indexOf('0') === 0) return digits.slice(1);
    return digits;
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.headingclaim}>
            <h4>Claimant details?</h4>
          </div>
        </Grid>
      </Grid>
      <ValidatorForm onSubmit={() => { }}>
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12}>
            <SelectValidator
              labelId="select-member"
              id="select-member"
              value={member}
              name="selectmember"
              type="text"
              label="Select from policy member"
              onChange={(e) => {
                // "Other" — claimant is not a policy member; clear the
                // pre-filled fields so they can be captured manually.
                if (e.target.value === "OTHER") {
                  setMember("OTHER");
                  setName("");
                  setIdNumber("");
                  setPhoneNumber("");
                  setRelationship("");
                  props.setAdditionalState("claimant_full_name", "");
                  props.setAdditionalState("claimant_id_number", "");
                  props.setAdditionalState("claimant_phone_number", "");
                  props.setAdditionalState("claimant_relationship_to_patient", "");
                  runDeceasedMatchCheck("", "", "");
                  runClaimantRelationshipCheck("", "", "", "");
                  return;
                }
                const fullPhoneNumber = e.target.value.dependant.msisdn;
                const n =
                  e.target.value.dependant.first_name +
                  " " +
                  e.target.value.dependant.last_name;
                const i = e.target.value.dependant.national_id;
                const r =
                  e.target.value.dependant.relationship === "Principal"
                    ? "Self"
                    : e.target.value.dependant.relationship;

                setMember(e.target.value);
                setName(
                  e.target.value.dependant.first_name +
                  " " +
                  e.target.value.dependant.last_name
                );
                setIdNumber(e.target.value.dependant.national_id);

                props.setAdditionalState("claimant_full_name", n);

                const localPhone = toLocalMsisdn(fullPhoneNumber);
                const fullPhone = toFullMsisdn(fullPhoneNumber);

                props.setAdditionalState("claimant_phone_number", fullPhone);
                setPhoneNumber(localPhone);

                props.setAdditionalState("claimant_id_number", i);
                props.setAdditionalState("claimant_relationship_to_patient", r);
                setRelationship(r);
                runDeceasedMatchCheck(n, i, fullPhone);
                runClaimantRelationshipCheck(n, i, fullPhone, r);
              }}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please select a member"]}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >
              {claimantDropdownMembers.map((entry, index) => (
                <MenuItem
                  key={index}
                  sx={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: 300,
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "0.005em",
                    color: "#000000",
                  }}
                  value={entry}
                >
                  {entry.dependant.first_name + " " + entry.dependant.last_name}
                </MenuItem>
              ))}
              <MenuItem
                value="OTHER"
                sx={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.005em",
                  color: "#000000",
                }}
              >
                Other
              </MenuItem>
            </SelectValidator>
            <hr />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="name"
              style={{ width: "100%" }}
              label="Full Names"
              name="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                props.setAdditionalState("claimant_full_name", e.target.value);
              }}
              onBlur={() => {
                runDeceasedMatchCheck(name, idNumber, phoneNumber);
                runClaimantRelationshipCheck(name, idNumber, phoneNumber, relationship);
              }}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please enter you full names"]}
            />
          </Grid>
          {partnerGuid === 'old_mutual_malawi' && claimantMatchesDeceasedError && (
            <Grid item xs={12}>
              <p style={{ color: 'red', fontSize: '12px', margin: '0 8px' }}>{claimantMatchesDeceasedError}</p>
            </Grid>
          )}
          {isHospiCashClaim && claimantRelationshipMismatchError && (
            <Grid item xs={12}>
              <p style={{ color: 'red', fontSize: '12px', margin: '0 8px' }}>{claimantRelationshipMismatchError}</p>
            </Grid>
          )}
          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="phone"
              style={{ width: "100%" }}
              label="Phone"
              name="phone"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                props.setAdditionalState(
                  "claimant_phone_number",
                  toFullMsisdn(e.target.value)
                );
              }}
              onBlur={() => {
                runDeceasedMatchCheck(name, idNumber, phoneNumber);
                runClaimantRelationshipCheck(name, idNumber, phoneNumber, relationship);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    sx={{
                      fontSize: "small",
                      color: "#000",
                      background: "#F7F7F7",
                    }}
                    position="start"
                  >
                    {" "}
                    <span
                      style={{
                        fontSize: "small",
                        color: "#000",
                      }}
                    >
                      {internationalCallingCode ? internationalCallingCode : ""}
                    </span>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              validators={["isValidPhoneNumber"]}
              errorMessages={[
                `Please enter a valid ${getLocalNumberLength()} digit phone number`,
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="id"
              style={{ width: "100%" }}
              label="Id number"
              name="id"
              value={idNumber}
              onChange={(e) => {
                setIdNumber(e.target.value);
                props.setAdditionalState("claimant_id_number", e.target.value);
              }}
              onBlur={() => {
                runDeceasedMatchCheck(name, idNumber, phoneNumber);
                runClaimantRelationshipCheck(name, idNumber, phoneNumber, relationship);
              }}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please enter you id number"]}
            />
          </Grid>
          {(partnerGuid === "omk" || partnerGuid === "omu_flp" || partnerGuid === "old_mutual_malawi") && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="email"
                style={{ width: "100%" }}
                label="Email (optional)"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  props.setAdditionalState("claimant_email", e.target.value);
                }}
                variant="outlined"
                // Email is optional; only validate the format once something is
                // entered so an empty field does not block submission.
                validators={email ? ["isEmail"] : []}
                errorMessages={email ? ["Please enter a valid email address"] : []}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-relationship"
              id="select-relationship"
              value={relationship}
              label="Relationship"
              name="relationships"
              onChange={(event) => {
                setRelationship(event.target.value);
                props.setAdditionalState(
                  "claimant_relationship_to_patient",
                  event.target.value
                );
                runClaimantRelationshipCheck(name, idNumber, phoneNumber, event.target.value);
              }}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
              variant="outlined"
              InputLabelProps={{
                shrink: !!relationship,
              }}
            >
              {relationshipOptions
                .map(([key, value]) => {
                  return (
                    <MenuItem
                      key={value}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      //disabled={principalAdded}
                      value={key}
                    >
                      {_.startCase(key)}
                    </MenuItem>
                  );
                })}
            </SelectValidator>
          </Grid>
          {(partnerGuid === "omk" || partnerGuid === "omu_flp") && (
            <Grid item xs={12} sm={6} md={6}>
              <SelectValidator
                labelId="payment-option"
                id="payment-option"
                value={paymentOption}
                name="paymentOption"
                type="text"
                label="Select Payment Option"
                onChange={(e) => {
                  setPaymentOption(e.target.value);
                  props.setAdditionalState(
                    "claimant_payment_option",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please select a payment option"]}
                style={{ width: "100%" }}
                sx={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.005em",
                  color: "#000000",
                }}
              >
                {(partnerGuid === "omk" && OldMutualKenyaPaymentOptions ? OldMutualKenyaPaymentOptions : partnerGuid === "omu_flp" && OldMutualFLPPaymentOptions).map((option, index) => {
                  return (
                    <MenuItem
                      key={index}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  );
                })}
              </SelectValidator>
            </Grid>
          )}
          {paymentOption === "Bank" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="bank-name"
                style={{ width: "100%" }}
                label="Bank Name"
                name="bankName"
                value={bankName}
                onChange={(e) => {
                  setBankName(e.target.value);
                  props.setAdditionalState(
                    "claimant_bank_name",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a bank name"]}
              />
            </Grid>
          )}
          {paymentOption === "Bank" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="account-number"
                style={{ width: "100%" }}
                label="Account Number"
                name="accountNumber"
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  props.setAdditionalState(
                    "claimant_bank_account_number",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a valid account number"]}
              />
            </Grid>
          )}

          {
            paymentOption === "Bank" && (
              <Grid item xs={12} md={6} sm={6}>
                <TextValidator
                  id="account-holder"
                  style={{ width: "100%" }}
                  label="Account Holder Name"
                  name="accountHolder"
                  value={claimant_bank_account_holder}
                  onChange={(e) => {
                    setClaimantBankAccountHolder(e.target.value);
                    props.setAdditionalState(
                      "claimant_bank_account_holder",
                      e.target.value
                    );
                  }}
                  variant="outlined"
                  validators={["required"]}
                  errorMessages={["Please enter the account holder's name"]}
                />
              </Grid>
            )
          }

          {
            paymentOption === "Bank" && (
              <Grid item xs={12} md={6} sm={6}>
                <TextValidator
                  id="bank-branch-code"
                  style={{ width: "100%" }}
                  label="Bank Branch Code"
                  name="bankBranchCode"
                  value={claimant_bank_branch_code}
                  onChange={(e) => {
                    setClaimantBankBranchCode(e.target.value);
                    props.setAdditionalState(
                      "claimant_bank_branch_code",
                      e.target.value
                    );
                  }}
                  variant="outlined"
                  validators={["required"]}
                  errorMessages={["Please enter the bank branch code"]}
                />
              </Grid>
            )
          }

          {paymentOption === "Mpesa" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="mpesa-number"
                style={{ width: "100%" }}
                label="Mpesa Number"
                name="mpesaNumber"
                value={mpesaNumber}
                onChange={(e) => {
                  setMpesaNumber(e.target.value);
                  props.setAdditionalState(
                    "claimant_mpesa_phone_number",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a valid mpesa number"]}
              />
            </Grid>
          )}
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default ClaimLoggingDetails